import HighQualityIcon from '@mui/icons-material/HighQuality';
import LaunchIcon from '@mui/icons-material/Launch';
import LensIcon from "@mui/icons-material/Lens";
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import { Box, Typography, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { DateTime } from 'luxon';
import { type FC, useEffect, useState } from 'react';
import {useNavigate} from 'react-router-dom';

import StreamCamerasMenu from './StreamCamerasMenu.js';
import StreamLogsMenu from './StreamLogsMenu.js';
import {useLatestLog} from '../../api/index.js';
import { type Camera } from '../../models/index.js';
import { capitalize, type WithRequired } from '../../utils/index.js';
import CameraIrToggleButton from '../Camera/CameraIrToggleButton.js';

type LatestLog = {
  dateTime: string;
  description: string;
  timeAgo: string;
};

interface StreamControlsProps {
  patientName: string;
  isHovered: boolean;
  displayedCameraName: string;
  onPause?: () => void;
  onPlay?: () => void;
  isPlaying?: boolean;
  isHQ: boolean;
  setIsHQ: () => void;
  cameras?: Camera[];
  setCamera: (camera: WithRequired<Partial<Camera>, 'id'>) => void;
  cameraId: string;
  patientId: string;
  lastActivity?: string;
  inMonitoringHours: boolean;
}

const StreamControls: FC<StreamControlsProps> = ({
                                                   patientName,
                                                   isHovered,
                                                   displayedCameraName,
                                                   isPlaying,
                                                   onPlay,
                                                   onPause,
                                                   isHQ,
                                                   setIsHQ,
                                                   cameras,
                                                   setCamera,
                                                   cameraId,
                                                   patientId,
                                                   lastActivity,
                                                   inMonitoringHours,
                                                 }) => {
  const [expansionLocked, setExpansionLocked] = useState(false);

  // IR control is Axis-only — NVR-attached cameras (Reolink) don't speak VAPIX.
  const currentCamera = cameras?.find((c) => c.id === cameraId);
  const isAxisCamera = !!currentCamera && currentCamera.nvrId == null && currentCamera.channel == null;

  const toggleVideo = (shouldPlay: boolean) => {
    if (shouldPlay) {
      if (onPlay) onPlay();
    } else {
      if (onPause) onPause();
    }
  };

  const navigate = useNavigate();

  const [formattedActivity, setFormattedActivity] = useState<string>();

  // LOGS
  const [latestLog, setLatestLog] = useState<LatestLog>();
  const logs = useLatestLog(patientId, {
    cacheTime: 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const updateLatestLog = () => {
      if (logs.data) {
        const _latestLog = logs.data;
        const dateTime = DateTime.fromISO(_latestLog.createdAt!);

        setLatestLog(() => ({
          dateTime: dateTime.toFormat('LLL dd, t'),
          description: capitalize(_latestLog.code!.description),
          timeAgo: dateTime.diffNow(['days', 'hours', 'minutes']).toHuman({unitDisplay: 'narrow', maximumFractionDigits: 0}).replace(/-?,?/g, ''),
        }));
      }
    };

    // We update LatestLog when they are initially fetched or refetched
    updateLatestLog();

    // We ALSO update LatestLog every minute so the minutesAgo stays up-to-date
    const interval = setInterval(() => {
      updateLatestLog();
    }, 60000); // One minute

    return () => clearInterval(interval);
  }, [logs.dataUpdatedAt]);
  /// END LOGS

  useEffect(() => {
    const updateLastActivity = () => {
      if (lastActivity) {
        const dateTime = DateTime.fromISO(lastActivity);
        const timeAgo = dateTime.diffNow(['hours', 'minutes', 'seconds']).toHuman({unitDisplay: 'narrow', maximumFractionDigits: 0}).replace(/-?,?/g, '');
        setFormattedActivity(timeAgo);
      }
      setFormattedActivity(undefined);
    };

    updateLastActivity();
    // We ALSO update LatestLog every minute so the minutesAgo stays up-to-date
    const interval = setInterval(() => {
      updateLastActivity();
    }, 1000); // 5 seconds
    return () => clearInterval(interval);
  }, [lastActivity]);

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          zIndex: 7,
          p: 1,
          display: 'flex',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          color: 'white',
          height: '3.4rem',
          flexWrap: 'nowrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          ...((isHovered || expansionLocked) && {width: '100%'}),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            ...((isHovered || expansionLocked) && {flexShrink: 1, minWidth: 0}),
          }}
        >
          <Typography
            noWrap
            component={'div'}
            sx={{color: 'white', margin: 0, overflow: 'initial', textOverflow: 'unset'}}
          >
            {patientName}
          </Typography>
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', ml: 0.25}}>{formattedActivity}
            <LensIcon color={inMonitoringHours ? 'success' : 'error'} sx={{fontSize: '1.2rem'}} />
          </Box>
          {latestLog && (
            <Typography noWrap sx={{opacity: 0.6, ml: 1, flexShrink: 100}}>
              {latestLog.timeAgo}
              {" "}
              {latestLog.description}
            </Typography>
          )}
        </Box>

        {isHovered || expansionLocked ? (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            {cameras?.length ? (
              <StreamCamerasMenu
                cameras={cameras}
                setCamera={setCamera}
                displayedCameraName={displayedCameraName}
                setExpansionLocked={setExpansionLocked}
              />
            ) : null}
            <StreamLogsMenu setExpansionLocked={setExpansionLocked} patientId={patientId} refetchLogs={logs.refetch}/>
            <Tooltip arrow color="primary" placement="bottom" title={`Turn Video ${isPlaying ? 'off' : 'on'}`}>
              <IconButton sx={{color: 'white'}} onClick={() => toggleVideo(!isPlaying)}>
                {isPlaying ? <VideocamOffIcon/> : <VideocamIcon/>}
              </IconButton>
            </Tooltip>
            <Tooltip
              arrow
              color="primary"
              placement="bottom"
              title={`Change Video quality to ${isHQ ? 'low' : 'high'} quality`}
            >
              <IconButton sx={{color: isHQ ? '#fac413' : 'white'}} onClick={() => setIsHQ()}>
                <HighQualityIcon/>
              </IconButton>
            </Tooltip>
            {isAxisCamera ? (
              <CameraIrToggleButton
                cameraId={cameraId}
                irCutFilter={currentCamera?.irCutFilter}
                irIlluminator={currentCamera?.irIlluminator}
                overlay
              />
            ) : null}
            <Tooltip arrow color="primary" placement="bottom" title={'Open Patient Profile'}>
              <IconButton sx={{color: 'white'}} onClick={() => navigate(`/patients/${patientId}`)}>
                <LaunchIcon/>
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}
      </Box>
      {isHovered ? <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 7,
            m: 1,
            display: 'flex',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            color: 'white',
            height: '2rem',
            flexWrap: 'nowrap',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {formattedActivity && (
            <Typography noWrap sx={{opacity: 0.6, ml: 1, flexShrink: 100}}>
              {formattedActivity}
            </Typography>
          )}
        </Box>
        : null}
    </>
  );
};

export default StreamControls;
